<template>
  <div class="about-publisher__wrapper">
    <a-visibility show :on="[$breakpoint.mobile]">
      <div class="about-publisher__title">
        About the Author:
        <a-author v-bind="publisher" class="author-text__subheading" />
      </div>
    </a-visibility>
    <div class="about-publisher__grid-wrapper">
      <div class="about-publisher__image">
        <a-image
          :src="publisher.imageUrl"
          :alt="publisher.name"
          :width="120"
          :aspect-ratio="$options.consts.aspectRatios.noRatio"
          :container-aspect-ratio="$options.consts.aspectRatios.userPhoto"
          :url="publisher.link"
          :force-url="true"
          :rounded="true"
        />
        <a-visibility show :on="[$breakpoint.mobile]">
          <publisher-counters
            :articles-count="publisher.articlesCount"
            :followers-count="publisher.followersCount"
            class="about-publisher__publisher-counters"
          />
        </a-visibility>
      </div>
      <div class="about-publisher__text-content">
        <a-visibility hide :on="[$breakpoint.mobile]">
          <div class="about-publisher__title">
            About the Author:
            <a-author v-bind="publisher" class="author-text__subheading" />
          </div>
        </a-visibility>
        <div class="about-publisher__bio">
          {{ publisher.bio }}
        </div>
      </div>

      <div class="about-publisher__status-and-actions">
        <a-visibility hide :on="[$breakpoint.mobile]">
          <publisher-counters
            :articles-count="publisher.articlesCount"
            :followers-count="publisher.followersCount"
          />
        </a-visibility>

        <a-button
          v-if="isButtonVisible"
          class="about-publisher__follow-button rounded-corners"
          :in-progress="inProgress"
          @click="onClick"
        >
          {{ buttonLabel }}
        </a-button>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'

import { PROP_TYPES, propValidator } from '@/utils/validators'
import mixins from '@/utils/mixins'
import AAuthor from 'shared/AAuthor'
import PublisherCounters from './PublisherCounters'
import { USER_STATUS, USER_TYPE } from 'enums/users'
import { aspectRatios } from 'enums/aspectRatios'
import { METHOD_NAME, PENDING_ACTION } from 'enums/pending-actions'

export default {
  name: 'ArticleAboutPublisher',
  mixins: [
    mixins.urlGenerators,
    mixins.pendingAuthActions,
    mixins.refreshDataListenersFactory({
      startRefreshHandlerByType: {
        [PENDING_ACTION.FOLLOW_AUTHOR]: METHOD_NAME.HANDLE_START_PENDING_ACTION
      },
      endRefreshHandlerByType: {
        [PENDING_ACTION.FOLLOW_AUTHOR]: METHOD_NAME.HANDLE_END_PENDING_ACTION
      }
    })
  ],
  components: { AAuthor, PublisherCounters },
  props: {
    publisher: propValidator([PROP_TYPES.OBJECT])
  },
  consts: { aspectRatios },
  data() {
    return {
      inProgress: false
    }
  },
  computed: {
    ...mapGetters({
      userDetails: 'auth/userDetails'
    }),
    isButtonVisible() {
      return (
        this.publisher.id !== this.userDetails.Id &&
        this.publisher.status === USER_STATUS.ACTIVE &&
        this.publisher.role !== USER_TYPE.SUBSCRIBER
      )
    },
    buttonLabel() {
      return this.publisher.isFollowed ? 'Unfollow' : 'Follow'
    }
  },
  methods: {
    onClick() {
      const actionData = {
        id: this.publisher.id,
        newValue: !this.publisher.isFollowed
      }

      this.$_pendingAuthActions_handleAction({
        actionType: PENDING_ACTION.FOLLOW_AUTHOR,
        actionData
      })
    },
    [METHOD_NAME.HANDLE_START_PENDING_ACTION](refreshPayload) {
      if (refreshPayload.id === this.publisher.id) {
        this.inProgress = true
      }
    },
    [METHOD_NAME.HANDLE_END_PENDING_ACTION](refreshPayload) {
      if (refreshPayload.id === this.publisher.id) {
        this.inProgress = false
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '~assets/scss/local/mixins/grid';
@import '~assets/scss/local/styles/article';

.about-publisher__wrapper {
  border: 1px solid $c--gray-light;
  border-radius: 5px;
  padding: 15px;

  @include mobile {
    padding: 15px 10px;
  }

  .about-publisher__grid-wrapper {
    $grid-setup: (
      $columnSetup: (
        $above-desktop-xl: 110px minmax(0, 100%) 165px,
        $desktop-xl: 110px minmax(0, 100%),
        $mobile: 85px minmax(0, 100%)
      ),
      $columnGapSetup: (
        $above-desktop-xl: 25px
      )
    );

    @include gridBuilder($grid-setup);
  }

  .about-publisher__publisher-counters {
    margin-top: 15px;
  }

  .about-publisher__title {
    @extend .about-the-author-title;

    $margin-bottom: (
      $above-desktop-xl: 10px,
      $mobile: 15px
    );

    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
  }

  .about-publisher__bio {
    $margin-bottom: (
      $above-desktop-xl: 15px,
      $mobile: 0
    );

    @extend .about-the-author-body;
    @include setStyleByBreakpoint('margin-bottom', $margin-bottom);
  }

  .about-publisher__status-and-actions {
    display: flex;
    $flex-direction: (
      $above-desktop-xl: column,
      $desktop-xl: row
    );
    $align-items: (
      $above-desktop-xl: flex-end,
      $desktop-xl: flex-start
    );
    $grid-area: (
      $desktop-xl: #{2} / #{2} / #{3} / #{3}
    );
    $margin-top: (
      $desktop-xl: 20px
    );

    @include setStyleByBreakpoint('flex-direction', $flex-direction);
    @include setStyleByBreakpoint('align-items', $align-items);
    @include setStyleByBreakpoint('grid-area', $grid-area);
    @include setStyleByBreakpoint('margin-top', $margin-top);
  }

  .about-publisher__status-wrapper {
    li {
      font-size: 16px;
      line-height: 18px;
      font-weight: 600;
    }
  }

  .about-publisher__follow-button {
    margin-top: auto;
    text-transform: uppercase;

    $margin-left: (
      $desktop-xl: 40px,
      $mobile: 0
    );

    @include setStyleByBreakpoint('margin-left', $margin-left);
  }
}
</style>
